import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, signal, inject, computed, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CommonModule } from '@angular/common';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { AvatarModule } from 'primeng/avatar';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from '@/shared/components/toast/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { HeaderDivisor } from '@/shared/components/layout/header-divisor/header-divisor';
import { BancoDetalhe } from './dialogs/banco-detalhe/banco-detalhe';
import { ContaDetalhe } from './dialogs/conta-detalhe/conta-detalhe';
import { TipoConvenioDetalhe } from './dialogs/tipo-convenio-detalhe/tipo-convenio-detalhe';
import { ConvenioDetalhe } from './dialogs/convenio-detalhe/convenio-detalhe';
import { Badge } from 'primeng/badge';
import { FormsModule } from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { BancosService } from '@/core/api/sicom-api/financeiro/arrecadacao/bancos/bancos.service';
import { BancoContaService } from '@/core/api/sicom-api/financeiro/arrecadacao/banco-conta/banco-conta.service';
import { ConvenioService } from '@/core/api/sicom-api/financeiro/arrecadacao/convenio/convenio.service';
import { TipoConvenioService } from '@/core/api/sicom-api/financeiro/arrecadacao/tipo-convenio/tipo-convenio.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

export interface Banco {
    id: string;
    codigo: string;
    nome: string;
}

export interface Conta {
    id: string;
    parentId: string;
    banco: string;
    agencia: string;
    agenciaDv: string;
    conta: string;
    contaDv: string;
    compartilhada: boolean;
    finalidade: string;
    usuario: string;
    descricao: string;
}

export interface Convenio {
    id: string;
    parentId: string;
    usuario: string;
    convenio: string;
    tipoGeracao: string;
    numeroCarteira: string;
    variacao: string;
    codigoCedente: string;
    compartilhado: boolean;
    tipoConvenio: string;
    coTipoConvenio?: number | null;
    tipoConvenioRemessa: string;
    status: string;
    registrado: boolean;
    permanenciaBoleto: string;
    apelido: string;
    codigoCedenteCfm: string;
    numeroAgenciaCfm: string;
    percentualRateioCfm: number;
}

export interface TipoConvenio {
    id: string;
    parentId: string;
    usuario: string;
    sigla: string;
    wsdlBoleto: boolean;
    coTipoConvenio?: number;
}

export type CardItem = Banco | Conta | Convenio;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-banco-conta-convenio',
    standalone: true,
    imports: [
        CardContainer,
        CommonModule,
        ButtonModule,
        TableModule,
        TooltipModule,
        CardContent,
        AvatarModule,
        HeaderDivisor,
        Toast,
        ConfirmDialogModule,
        BancoDetalhe,
        ContaDetalhe,
        ConvenioDetalhe,
        TipoConvenioDetalhe,
        Badge,
        FormsModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        CanDirective
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './banco-conta-convenio.html',
    styleUrl: './banco-conta-convenio.scss'
})
/** Configuração de bancos, contas e convênios (abas + grades) com os respectivos modais de detalhe. */
export class BancoContaConvenio implements OnInit {
    protected readonly roleBancosEditar = PERMISSOES.configuracoes.bancos.editar;
    protected readonly roleBancosExcluir = PERMISSOES.configuracoes.bancos.excluir;

    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly bancosService = inject(BancosService);
    private readonly bancoContaService = inject(BancoContaService);
    private readonly convenioService = inject(ConvenioService);
    private readonly tipoConvenioService = inject(TipoConvenioService);
    private readonly destroyRef = inject(DestroyRef);

    selectedBancoId = signal<string | null>(null);
    selectedContaId = signal<string | null>(null);
    expandedBankId = signal<string | null>(null);

    modalBanco = signal(false);
    selectedBanco = signal<Banco | null>(null);

    modalConta = signal(false);
    selectedConta = signal<Conta | null>(null);

    modalConvenio = signal(false);
    selectedConvenio = signal<Convenio | null>(null);

    modalTipoConvenio = signal(false);
    selectedTipoConvenio = signal<TipoConvenio | null>(null);

    protected bancos = signal<Banco[]>([]);
    protected contas = signal<Conta[]>([]);
    protected convenios = signal<Convenio[]>([]);
    protected tiposConvenio = signal<TipoConvenio[]>([]);

    filtroBancos = signal('');
    filtroContas = signal('');
    filtroConvenios = signal('');

    somenteBancosComContas = signal(false);
    bancosComContasIds = signal<Set<string>>(new Set());
    bancosComContasCarregados = signal(false);
    carregandoFiltroContas = signal(false);

    columns = computed(() => {
        const termoBanco = this.filtroBancos().toLowerCase().trim();
        const termoConta = this.filtroContas().toLowerCase().trim();
        const termoConvenio = this.filtroConvenios().toLowerCase().trim();

        const bancosFiltrados = (termoBanco ? this.bancos().filter((b) => b.nome.toLowerCase().includes(termoBanco) || b.codigo.toLowerCase().includes(termoBanco)) : this.bancos()).filter(
            (b) => !this.somenteBancosComContas() || this.bancosComContasIds().has(b.id)
        );

        const contasFiltradas = this.contas()
            .filter((c) => c.parentId === this.selectedBancoId())
            .filter((c) => !termoConta || [c.agencia, c.conta, c.finalidade].some((v) => (v ?? '').toLowerCase().includes(termoConta)));

        const conveniosFiltrados = this.convenios()
            .filter((c) => c.parentId === this.selectedContaId())
            .filter((c) => !termoConvenio || [c.convenio, c.apelido, c.codigoCedente].some((v) => (v ?? '').toLowerCase().includes(termoConvenio)));

        return [
            { id: 'bancos', title: 'BANCOS', color: 'bg-indigo-50/20 dark:bg-indigo-900/10', cards: bancosFiltrados as CardItem[] },
            { id: 'contas', title: 'CONTAS', color: 'bg-amber-50/20 dark:bg-amber-900/10', cards: contasFiltradas as CardItem[] },
            { id: 'convenios', title: 'CONVÊNIOS', color: 'bg-emerald-50/20 dark:bg-emerald-900/10', cards: conveniosFiltrados as CardItem[] }
        ];
    });

    ngOnInit(): void {
        this.carregarBancos();
    }

    private carregarBancos(): void {
        this.bancosService
            .list()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) => this.bancos.set(lista.map((b) => ({ id: b.id, codigo: b.id, nome: b.descricao }))),
                error: () => undefined
            });
    }

    private carregarContas(codigoBanco: string): void {
        this.bancoContaService
            .listarPorBanco(codigoBanco)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) =>
                    this.contas.set(
                        lista.map((c) => ({
                            id: String(c.id),
                            parentId: c.codigoBanco,
                            banco: c.agencia && c.conta ? `${c.agencia}-${c.conta}` : `Conta ${c.id}`,
                            agencia: c.agencia ?? '',
                            agenciaDv: c.agenciaDv ?? '',
                            conta: c.conta ?? '',
                            contaDv: c.contaDv ?? '',
                            compartilhada: false,
                            finalidade: c.finalidade ?? '',
                            usuario: '',
                            descricao: ''
                        }))
                    ),
                error: () => undefined
            });
    }

    private carregarConvenios(bancoContaId: number): void {
        this.convenioService
            .listarPorBancoConta(bancoContaId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) =>
                    this.convenios.set(
                        lista.map((c) => ({
                            id: c.id,
                            parentId: String(c.idBancoConta ?? ''),
                            usuario: '',
                            convenio: c.nuConvenio ?? '',
                            tipoGeracao: c.inRemessaBoleto ?? '',
                            numeroCarteira: String(c.nuCarteira ?? ''),
                            variacao: String(c.nuVariacao ?? ''),
                            codigoCedente: c.coBeneficiario ?? '',
                            compartilhado: c.inCompartilhado ?? false,
                            tipoConvenio: c.sgTipoConvenio ?? '',
                            coTipoConvenio: c.coTipoConvenio,
                            tipoConvenioRemessa: c.inRemessaBoleto ?? '',
                            status: c.inStatus ?? '',
                            registrado: c.inRegistro ?? false,
                            permanenciaBoleto: String(c.nuDiasPermanencia ?? ''),
                            apelido: c.nmApelido ?? '',
                            codigoCedenteCfm: c.coBeneficiarioCfm ?? '',
                            numeroAgenciaCfm: c.nuAgenciaCfm ?? '',
                            percentualRateioCfm: Number(c.peRateioCfm ?? 0)
                        }))
                    ),
                error: () => undefined
            });
    }

    private carregarTiposConvenio(codigoBanco: string): void {
        this.tipoConvenioService
            .listarPorBanco(codigoBanco)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) =>
                    this.tiposConvenio.set(
                        lista.map((tc) => ({
                            id: tc.id,
                            parentId: tc.codigoBanco,
                            usuario: '',
                            sigla: tc.sigla ?? '',
                            wsdlBoleto: tc.inWsBoleto ?? false,
                            coTipoConvenio: tc.coTipoConvenio
                        }))
                    ),
                error: () => undefined
            });
    }

    asBanco(card: CardItem): Banco {
        return card as Banco;
    }
    asConta(card: CardItem): Conta {
        return card as Conta;
    }
    asConvenio(card: CardItem): Convenio {
        return card as Convenio;
    }

    countContas(bancoId: string): number {
        return this.contas().filter((c) => c.parentId === bancoId).length;
    }

    countTiposConvenio(bancoId: string): number {
        return this.tiposConvenio().filter((tc) => tc.parentId === bancoId).length;
    }

    listarTiposConvenioPorBanco(bancoId: string): TipoConvenio[] {
        return this.tiposConvenio().filter((tc) => tc.parentId === bancoId);
    }

    tiposConvenioParaForm(): TipoConvenio[] {
        return this.tiposConvenio().filter((tc) => tc.parentId === this.selectedBancoId());
    }

    isNaoInformado(valor: unknown): boolean {
        if (valor === null || valor === undefined) return true;
        if (typeof valor === 'string') return valor.trim().length === 0;
        return false;
    }

    textoOuNaoInformado(valor: unknown): string {
        return this.isNaoInformado(valor) ? 'Não Informado' : String(valor);
    }

    textoComDvOuNaoInformado(numero: unknown, dv: unknown): string {
        const n = this.isNaoInformado(numero) ? '' : String(numero).trim();
        const d = this.isNaoInformado(dv) ? '' : String(dv).trim();
        if (!n && !d) return 'Não Informado';
        if (n && d) return `${n}-${d}`;
        return n || d;
    }

    selecionarCard(colId: string, cardId: string): void {
        if (colId === 'bancos') {
            this.selectedBancoId.set(cardId);
            this.selectedContaId.set(null);
            this.convenios.set([]);
            this.carregarContas(cardId);
            this.carregarTiposConvenio(cardId);
        } else if (colId === 'contas') {
            this.selectedContaId.set(cardId);
            this.carregarConvenios(Number(cardId));
        }
    }

    toggleExpand(event: Event, id: string): void {
        event.stopPropagation();
        if (this.expandedBankId() === id) {
            this.expandedBankId.set(null);
        } else {
            this.expandedBankId.set(id);
            this.selecionarCard('bancos', id);
        }
    }

    isCardSelected(colId: string, cardId: string): boolean {
        if (colId === 'bancos') return this.selectedBancoId() === cardId;
        if (colId === 'contas') return this.selectedContaId() === cardId;
        return false;
    }

    editarCard(card: CardItem): void {
        if ('codigo' in card) {
            this.selectedBanco.set(card as Banco);
            this.modalBanco.set(true);
        } else if ('agencia' in card) {
            this.selectedConta.set(card as Conta);
            this.modalConta.set(true);
        } else {
            this.selectedConvenio.set(card as Convenio);
            this.modalConvenio.set(true);
        }
    }

    excluirCard(card: CardItem): void {
        const isBanco = 'codigo' in card;
        const isConta = 'agencia' in card;
        const nome = isBanco ? (card as Banco).nome : isConta ? (card as Conta).banco : (card as Convenio).convenio;

        if (isConta) {
            const conveniosLocais = this.convenios().filter((c) => c.parentId === card.id);
            if (conveniosLocais.length > 0) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Exclusão não permitida',
                    detail: 'Esta conta bancária possui parcelas vinculadas e não pode ser excluída.'
                });
                return;
            }

            this.convenioService
                .listarPorBancoConta(Number(card.id))
                .pipe(take(1))
                .subscribe({
                    next: (convenios) => {
                        if (convenios.length > 0) {
                            this.messageService.add({
                                severity: 'warn',
                                summary: 'Exclusão não permitida',
                                detail: 'Esta conta bancária possui parcelas vinculadas e não pode ser excluída.'
                            });
                            return;
                        }
                        this.confirmarExclusao(card, isBanco, isConta, nome);
                    },
                    error: () => undefined
                });
            return;
        }

        this.confirmarExclusao(card, isBanco, isConta, nome);
    }

    private confirmarExclusao(card: CardItem, isBanco: boolean, isConta: boolean, nome: string): void {
        this.confirmationService.confirm({
            message: `Tem certeza que deseja excluir o ${isBanco ? 'banco' : isConta ? 'conta' : 'convênio'} "${nome}"?`,
            header: 'Confirmação de Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                if (isBanco) {
                    this.bancosService
                        .deletar(card.id)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: () => {
                                this.bancos.update((list) => list.filter((b) => b.id !== card.id));
                                if (this.selectedBancoId() === card.id) {
                                    this.selectedBancoId.set(null);
                                    this.selectedContaId.set(null);
                                    this.contas.set([]);
                                    this.convenios.set([]);
                                }
                                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Banco excluído com sucesso!' });
                            },
                            error: () => undefined
                        });
                } else if (isConta) {
                    this.bancoContaService
                        .deletar(Number(card.id))
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: () => {
                                this.contas.update((list) => list.filter((c) => c.id !== card.id));
                                if (this.selectedContaId() === card.id) {
                                    this.selectedContaId.set(null);
                                    this.convenios.set([]);
                                }
                                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Conta excluída com sucesso!' });
                            },
                            error: () => undefined
                        });
                } else {
                    const conv = card as Convenio;
                    const [coBanco, nuConvenio, inRemessaBoleto] = conv.id.split('|');
                    this.convenioService
                        .deletar(coBanco, nuConvenio, inRemessaBoleto)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: () => {
                                this.convenios.update((list) => list.filter((v) => v.id !== card.id));
                                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Convênio excluído com sucesso!' });
                            },
                            error: () => undefined
                        });
                }
            }
        });
    }

    onToggleBancosComContas(): void {
        // Desativar: imediato
        if (this.somenteBancosComContas()) {
            this.somenteBancosComContas.set(false);
            return;
        }

        // Ativar com cache completo já disponível: imediato
        if (this.bancosComContasCarregados()) {
            this.somenteBancosComContas.set(true);
            return;
        }

        // Ativar sem cache: carrega primeiro, ativa depois (evita piscar com Set vazio)
        const bancosAtual = this.bancos();
        if (bancosAtual.length === 0) {
            this.somenteBancosComContas.set(true);
            return;
        }

        this.carregandoFiltroContas.set(true);
        forkJoin(
            bancosAtual.map((b) =>
                this.bancoContaService.listarPorBanco(b.id).pipe(
                    map((contas) => ({ bancoId: b.id, temContas: contas.length > 0 })),
                    catchError(() => of({ bancoId: b.id, temContas: false }))
                )
            )
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (results) => {
                    this.bancosComContasIds.set(new Set(results.filter((r) => r.temContas).map((r) => r.bancoId)));
                    this.bancosComContasCarregados.set(true);
                    this.carregandoFiltroContas.set(false);
                    this.somenteBancosComContas.set(true); // ativa APÓS os dados chegarem
                },
                error: () => {
                    this.carregandoFiltroContas.set(false);
                }
            });
    }

    adicionarNovo(colId: string): void {
        if (colId === 'bancos') {
            this.selectedBanco.set(null);
            this.modalBanco.set(true);
        } else if (colId === 'contas') {
            if (!this.selectedBancoId()) {
                this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione um banco primeiro!' });
                return;
            }
            this.selectedConta.set(null);
            this.modalConta.set(true);
        } else {
            if (!this.selectedContaId()) {
                this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione uma conta primeiro!' });
                return;
            }
            this.selectedConvenio.set(null);
            this.modalConvenio.set(true);
        }
    }

    adicionarTipoConvenio(bancoId: string): void {
        this.selectedBancoId.set(bancoId);
        this.selectedTipoConvenio.set(null);
        this.modalTipoConvenio.set(true);
    }

    editarTipoConvenio(tipoConvenio: TipoConvenio): void {
        this.selectedTipoConvenio.set(tipoConvenio);
        this.modalTipoConvenio.set(true);
    }

    excluirTipoConvenio(tipoConvenio: TipoConvenio): void {
        this.confirmationService.confirm({
            message: `Tem certeza que deseja excluir o tipo de convênio "${tipoConvenio.sigla}"?`,
            header: 'Confirmação de Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                const coTipoConvenio = tipoConvenio.coTipoConvenio;
                if (!coTipoConvenio) return;
                this.tipoConvenioService
                    .deletar(tipoConvenio.parentId, coTipoConvenio)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            this.tiposConvenio.update((list) => list.filter((tc) => tc.id !== tipoConvenio.id));
                            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Tipo de convênio excluído com sucesso!' });
                        },
                        error: () => undefined
                    });
            }
        });
    }

    onBancoSalvo(banco: Banco): void {
        this.bancosService
            .salvar({ coBanco: banco.codigo, nmBanco: banco.nome })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resp) => {
                    const salvo: Banco = { id: resp.id, codigo: resp.id, nome: resp.descricao };
                    this.bancos.update((list) => {
                        const idx = list.findIndex((b) => b.id === salvo.id);
                        return idx >= 0 ? list.map((b) => (b.id === salvo.id ? salvo : b)) : [...list, salvo];
                    });
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Banco salvo com sucesso!' });
                },
                error: () => undefined
            });
    }

    onContaSalvo(conta: Conta): void {
        const request = {
            id: conta.id ? Number(conta.id) : null,
            codigoBanco: this.selectedBancoId()!,
            agencia: conta.agencia,
            agenciaDv: conta.agenciaDv,
            conta: conta.conta,
            contaDv: conta.contaDv,
            cedente: null,
            finalidade: conta.finalidade
        };
        this.bancoContaService
            .salvar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resp) => {
                    const salva: Conta = {
                        id: String(resp.id),
                        parentId: resp.codigoBanco,
                        banco: resp.agencia && resp.conta ? `${resp.agencia}-${resp.conta}` : `Conta ${resp.id}`,
                        agencia: resp.agencia ?? '',
                        agenciaDv: resp.agenciaDv ?? '',
                        conta: resp.conta ?? '',
                        contaDv: resp.contaDv ?? '',
                        compartilhada: false,
                        finalidade: resp.finalidade ?? '',
                        usuario: '',
                        descricao: ''
                    };
                    this.contas.update((list) => {
                        const idx = list.findIndex((c) => c.id === salva.id);
                        return idx >= 0 ? list.map((c) => (c.id === salva.id ? salva : c)) : [...list, salva];
                    });
                    const bancoId = this.selectedBancoId();
                    if (bancoId && !this.bancosComContasIds().has(bancoId)) {
                        this.bancosComContasIds.update((ids) => new Set([...ids, bancoId]));
                    }
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Conta salva com sucesso!' });
                },
                error: () => undefined
            });
    }

    onConvenioSalvo(convenio: Convenio): void {
        const [coBanco, nuConvenio, inRemessaBoleto] = convenio.id ? convenio.id.split('|') : ['', '', ''];
        const request = {
            codigoBanco: coBanco || this.selectedBancoId()!,
            nuConvenio: nuConvenio || convenio.convenio,
            inRemessaBoleto: inRemessaBoleto || convenio.tipoGeracao,
            idBancoConta: Number(this.selectedContaId()),
            coTipoConvenio: convenio.coTipoConvenio ?? null,
            nuCarteira: Number(convenio.numeroCarteira) || null,
            nuVariacao: Number(convenio.variacao) || null,
            inCompartilhado: convenio.compartilhado,
            inStatus: convenio.status,
            coBeneficiario: convenio.codigoCedente,
            inRegistro: convenio.registrado,
            coBeneficiarioCfm: convenio.codigoCedenteCfm,
            nuAgenciaCfm: convenio.numeroAgenciaCfm,
            peRateioCfm: convenio.percentualRateioCfm,
            nuDiasPermanencia: Number(convenio.permanenciaBoleto) || null,
            nmApelido: convenio.apelido
        };
        this.convenioService
            .salvar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resp) => {
                    const salvo: Convenio = {
                        id: resp.id,
                        parentId: String(resp.idBancoConta ?? ''),
                        usuario: '',
                        convenio: resp.nuConvenio ?? '',
                        tipoGeracao: resp.inRemessaBoleto ?? '',
                        numeroCarteira: String(resp.nuCarteira ?? ''),
                        variacao: String(resp.nuVariacao ?? ''),
                        codigoCedente: resp.coBeneficiario ?? '',
                        compartilhado: resp.inCompartilhado ?? false,
                        tipoConvenio: resp.sgTipoConvenio ?? '',
                        coTipoConvenio: resp.coTipoConvenio,
                        tipoConvenioRemessa: resp.inRemessaBoleto ?? '',
                        status: resp.inStatus ?? '',
                        registrado: resp.inRegistro ?? false,
                        permanenciaBoleto: String(resp.nuDiasPermanencia ?? ''),
                        apelido: resp.nmApelido ?? '',
                        codigoCedenteCfm: resp.coBeneficiarioCfm ?? '',
                        numeroAgenciaCfm: resp.nuAgenciaCfm ?? '',
                        percentualRateioCfm: Number(resp.peRateioCfm ?? 0)
                    };
                    this.convenios.update((list) => {
                        const idx = list.findIndex((v) => v.id === salvo.id);
                        return idx >= 0 ? list.map((v) => (v.id === salvo.id ? salvo : v)) : [...list, salvo];
                    });
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Convênio salvo com sucesso!' });
                },
                error: () => undefined
            });
    }

    onTipoConvenioSalvo(tipoConvenio: TipoConvenio): void {
        if (!this.selectedBancoId()) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione um banco primeiro!' });
            return;
        }
        const request = {
            coTipoConvenio: tipoConvenio.coTipoConvenio ?? null,
            codigoBanco: this.selectedBancoId()!,
            sigla: tipoConvenio.sigla,
            inWsBoleto: tipoConvenio.wsdlBoleto,
            inSituacao: 'A'
        };
        this.tipoConvenioService
            .salvar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resp) => {
                    const salvo: TipoConvenio = {
                        id: resp.id,
                        parentId: resp.codigoBanco,
                        usuario: '',
                        sigla: resp.sigla ?? '',
                        wsdlBoleto: resp.inWsBoleto ?? false,
                        coTipoConvenio: resp.coTipoConvenio
                    };
                    this.tiposConvenio.update((list) => {
                        const idx = list.findIndex((tc) => tc.id === salvo.id);
                        return idx >= 0 ? list.map((tc) => (tc.id === salvo.id ? salvo : tc)) : [...list, salvo];
                    });
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Tipo de convênio salvo com sucesso!' });
                },
                error: () => undefined
            });
    }
}
